// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { head } from 'lodash/fp';
import styled from '@emotion/styled';
import Button from 'app/components/commons/Button';
import * as userActions from 'app/actions/userActions';
import Spinner from 'app/components/commons/Spinner';
import DocumentDropZoneField from './DocumentDropZoneField';

type File = {
  preview: Image,
  name: string
};

type Props = {
  isUploading: boolean,
  isUploadingDocumentErrors: ?Array<Object>,
  uploadDocument: (file: File) => void
};

type State = {
  acceptedFile?: File,
  isFileRejected: boolean
};

const ButtonContainer = styled.div(
  {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1
  },
  props => ({
    paddingTop: props.theme.spacing.small
  })
);

const UploadButton = styled(Button)({
  borderTopRightRadius: '0 !important',
  borderBottomRightRadius: '0 !important'
});

const CancelButton = styled(Button)({
  maxWidth: 50,
  borderTopLeftRadius: '0 !important',
  borderBottomLeftRadius: '0 !important'
});

const UpArrow = styled(({ className }) => <span className={`${className} ion-android-arrow-up`} />)(
  {
    paddingLeft: 6
  }
);

const UploadingContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const UploadingLabel = styled.span({
  paddingRight: 6
});

class DocumentUploader extends React.Component<Props, State> {
  props: Props;

  state: State = {
    isFileRejected: false
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      Array.isArray(nextProps.isUploadingDocumentErrors) &&
      nextProps.isUploadingDocumentErrors.length > 0
    ) {
      this.setState({ isFileRejected: true });
    }
  }

  handleDocumentDrop = (accepted: Array<Object>, rejected: Array<Object>) => {
    const file = head(accepted || []);

    this.setState({
      acceptedFile: file
        ? Object.assign(file, {
            preview: URL.createObjectURL(file)
          })
        : undefined,
      isFileRejected: !!head(rejected)
    });
  };

  handleUploadClicked = () => {
    const { uploadDocument } = this.props;
    const { acceptedFile } = this.state;

    if (acceptedFile) uploadDocument(acceptedFile);
  };

  handleCancelClicked = () => {
    this.setState({
      acceptedFile: undefined,
      isFileRejected: false
    });
  };

  render() {
    const { isUploading } = this.props;
    const { acceptedFile, isFileRejected } = this.state;

    return (
      <div>
        <DocumentDropZoneField
          onDrop={this.handleDocumentDrop}
          acceptedFile={acceptedFile}
          displayRejectedMessage={isFileRejected}
        />
        <ButtonContainer>
          <UploadButton
            block
            size="medium"
            disabled={!acceptedFile}
            onClick={this.handleUploadClicked}
          >
            {isUploading ? (
              <UploadingContainer>
                <UploadingLabel>Uploading...</UploadingLabel>
                <Spinner />
              </UploadingContainer>
            ) : (
              <div>
                <span>Upload</span>
                <UpArrow />
              </div>
            )}
          </UploadButton>
          <CancelButton
            block
            size="medium"
            kind="delete"
            disabled={false}
            onClick={this.handleCancelClicked}
          >
            <span className="ion-close-round" />
          </CancelButton>
        </ButtonContainer>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    isUploading: state.user.uploadingDocument,
    isUploadingDocumentErrors: state.user.uploadingDocumentErrors
  };
}

const actions = {
  uploadDocument: userActions.uploadDocument
};

export default connect(
  mapStateToProps,
  actions
)(DocumentUploader);
